/**
 * Canonical currency constants and formatter for The Runup.
 *
 * The app operates exclusively in NIS (Israeli Shekel, ₪).
 * Stored monetary values are interpreted directly as ILS — no conversion.
 *
 * Use formatPrice() everywhere a price is displayed.
 * Use getPriceRange() for the ₪/₪₪/₪₪₪/₪₪₪₪/₪₪₪₪₪ tier display on vendor cards.
 */

import type { VendorCategoryType } from "../types/vendorCategories";
import { CATEGORY_PRICING_DEFAULTS } from "../config/categoryPricingDefaults";

export const CURRENCY = "ILS" as const;
export const CURRENCY_SYMBOL = "₪";
export const CURRENCY_LOCALE = "he-IL";
export type CurrencyCode = typeof CURRENCY;

// ─── Per-user display currency (demo accounts) ──────────────────────────────
// The app stores ALL amounts as plain numbers interpreted as ILS, except for
// explicitly-tagged demo accounts whose docs carry USD amounts and whose
// users/{uid}.currencyOverride === "USD". For those accounts the client sets
// the active display currency at auth; everyone else stays ILS.
//
// setActiveCurrency must ONLY be called from client ("use client") code.
// On the Next.js server this module is shared across requests, so server
// components must pass an explicit opts.currency to formatPrice/getPriceRange
// (the public quote page resolves currency from the quote doc this way).
// Mobile never calls the setter and stays ILS.
export type ActiveCurrency = "ILS" | "USD";

const CURRENCY_META: Record<ActiveCurrency, { symbol: string; locale: string }> = {
  ILS: { symbol: "₪", locale: "he-IL" },
  USD: { symbol: "$", locale: "en-US" },
};

let activeCurrency: ActiveCurrency = "ILS";

export function setActiveCurrency(currency: ActiveCurrency | null | undefined): void {
  activeCurrency = currency === "USD" ? "USD" : "ILS";
}

export function getActiveCurrencyCode(): ActiveCurrency {
  return activeCurrency;
}

export function getActiveCurrencySymbol(): string {
  return CURRENCY_META[activeCurrency].symbol;
}

/**
 * Demo-calibration rate used ONLY to map USD amounts onto the ILS-calibrated
 * getPriceRange tier thresholds. Not a real FX conversion — display amounts
 * are never converted.
 */
const USD_TIER_RATE = 3.7;

/**
 * Format a numeric ILS amount for display.
 *
 * @param amountILS - Amount in shekels (already in NIS — no conversion).
 * @param opts.compact - Use K/M abbreviation (e.g. ₪4.3K, ₪1.2M).
 * @param opts.showCents - Show two decimals (e.g. ₪1,234.50).
 */
export function formatPrice(
  amountILS: number,
  opts?: { compact?: boolean; showCents?: boolean; currency?: ActiveCurrency },
): string {
  const compact = opts?.compact ?? false;
  const showCents = opts?.showCents ?? false;
  const meta = CURRENCY_META[opts?.currency ?? activeCurrency];

  if (compact && Math.abs(amountILS) >= 1_000_000) {
    const m = amountILS / 1_000_000;
    return `${meta.symbol}${m.toFixed(Math.abs(m) >= 10 ? 0 : 1)}M`;
  }
  if (compact && Math.abs(amountILS) >= 1000) {
    const k = amountILS / 1000;
    return `${meta.symbol}${k.toFixed(Math.abs(k) >= 10 ? 0 : 1)}K`;
  }

  const formatted = new Intl.NumberFormat(meta.locale, {
    minimumFractionDigits: showCents ? 2 : 0,
    maximumFractionDigits: showCents ? 2 : 0,
  }).format(showCents ? amountILS : Math.round(amountILS));

  return `${meta.symbol}${formatted}`;
}

/**
 * Pricing-type-aware, category-aware price tier (₪ to ₪₪₪₪₪).
 *
 * Calibrated to Israeli wedding market 2026 averages.
 * For per_person pricing (Venue/Caterer), tiers reflect price per guest.
 * For flat pricing (Photographer/DJ/etc), tiers reflect total package price.
 * For hourly pricing, tiers reflect hourly rate.
 *
 * @param price - The starting price in ILS.
 * @param category - Vendor category (affects what "expensive" means).
 * @param pricingType - "per_person" | "flat" | "hourly". Defaults to "flat".
 */
export type PricingType = "per_person" | "flat" | "hourly";

export function getPriceRange(
  price: number,
  category?: VendorCategoryType,
  pricingType: PricingType = "flat",
  opts?: { currency?: ActiveCurrency },
): string {
  const currency = opts?.currency ?? activeCurrency;
  if (currency === "ILS") return getPriceRangeILS(price, category, pricingType);
  // USD amounts: scale onto the ILS-calibrated thresholds, re-symbol the tier.
  const ilsTier = getPriceRangeILS(price * USD_TIER_RATE, category, pricingType);
  return CURRENCY_META[currency].symbol.repeat(ilsTier.length);
}

function getPriceRangeILS(
  price: number,
  category?: VendorCategoryType,
  pricingType: PricingType = "flat",
): string {
  // Hourly applies across categories — DJ overtime, hourly hire, etc.
  if (pricingType === "hourly") {
    if (price < 500) return "₪";
    if (price < 800) return "₪₪";
    if (price < 1200) return "₪₪₪";
    if (price < 2000) return "₪₪₪₪";
    return "₪₪₪₪₪";
  }

  // Per-person pricing — scale varies by category:
  // - Venue (all-inclusive in Israel): ₪200/350/500/700 thresholds
  // - Caterer (standalone, cheaper since venue handles service/space): ₪100/175/275/400
  // - Baker (dessert table): ₪40/80/150/250
  if (pricingType === "per_person") {
    if (category === "Baker") {
      if (price < 40) return "₪";
      if (price < 80) return "₪₪";
      if (price < 150) return "₪₪₪";
      if (price < 250) return "₪₪₪₪";
      return "₪₪₪₪₪";
    }
    if (category === "Caterer") {
      // Standalone caterer is cheaper than all-inclusive venue per-head
      // (venue includes staff, rentals, space; caterer is food + cooking only).
      if (price < 100) return "₪";
      if (price < 175) return "₪₪";
      if (price < 275) return "₪₪₪";
      if (price < 400) return "₪₪₪₪";
      return "₪₪₪₪₪";
    }
    // Venue (and any unspecified per-person category — likely venue-like)
    // Skewed so ≥₪500/person lands in "very expensive" tier.
    if (price < 200) return "₪";
    if (price < 300) return "₪₪";
    if (price < 500) return "₪₪₪";
    if (price < 700) return "₪₪₪₪";
    return "₪₪₪₪₪";
  }

  // Flat pricing — thresholds vary by category.
  switch (category) {
    case "Photographer":
      if (price < 5_000) return "₪";
      if (price < 9_000) return "₪₪";
      if (price < 14_000) return "₪₪₪";
      if (price < 22_000) return "₪₪₪₪";
      return "₪₪₪₪₪";
    case "Videographer":
      if (price < 4_000) return "₪";
      if (price < 7_000) return "₪₪";
      if (price < 12_000) return "₪₪₪";
      if (price < 18_000) return "₪₪₪₪";
      return "₪₪₪₪₪";
    case "DJ":
      if (price < 4_000) return "₪";
      if (price < 6_000) return "₪₪";
      if (price < 9_000) return "₪₪₪";
      if (price < 13_000) return "₪₪₪₪";
      return "₪₪₪₪₪";
    case "Band":
      if (price < 10_000) return "₪";
      if (price < 18_000) return "₪₪";
      if (price < 25_000) return "₪₪₪";
      if (price < 40_000) return "₪₪₪₪";
      return "₪₪₪₪₪";
    case "Florist":
    case "Decor":
      if (price < 3_000) return "₪";
      if (price < 7_000) return "₪₪";
      if (price < 15_000) return "₪₪₪";
      if (price < 30_000) return "₪₪₪₪";
      return "₪₪₪₪₪";
    case "Baker":
      if (price < 700) return "₪";
      if (price < 1_500) return "₪₪";
      if (price < 3_000) return "₪₪₪";
      if (price < 5_000) return "₪₪₪₪";
      return "₪₪₪₪₪";
    case "Venue":
      // Flat venue pricing is rare in Israel (most are per-person all-inclusive),
      // but possible for flat rental.
      if (price < 30_000) return "₪";
      if (price < 75_000) return "₪₪";
      if (price < 150_000) return "₪₪₪";
      if (price < 300_000) return "₪₪₪₪";
      return "₪₪₪₪₪";
    case "Caterer":
      // Standalone caterer flat pricing — roughly half of venue flat
      // (caterer = food + cooking; venue = food + space + service + rentals).
      if (price < 15_000) return "₪";
      if (price < 35_000) return "₪₪";
      if (price < 70_000) return "₪₪₪";
      if (price < 150_000) return "₪₪₪₪";
      return "₪₪₪₪₪";
    default:
      // Unknown category — generic flat scale.
      if (price < 3_000) return "₪";
      if (price < 7_500) return "₪₪";
      if (price < 15_000) return "₪₪₪";
      if (price < 30_000) return "₪₪₪₪";
      return "₪₪₪₪₪";
  }
}

/**
 * Maximum allowed budget across the app, in ILS.
 * Used by event creation budget pickers (mobile + web).
 */
export const MAX_EVENT_BUDGET_ILS = 200_000;

/**
 * Resolve the effective pricing type for a vendor — uses the explicit
 * `pricingType` if set, else falls back to the category's `defaultPricingType`
 * (Venue/Caterer → per_person, DJ → flat, etc.).
 *
 * Critical for production data where `pricingType` is often unset on vendors,
 * but the category strongly implies the model (every Israeli wedding venue
 * is per-person whether or not the vendor explicitly tagged that).
 */
export function getEffectivePricingType(
  category?: string,
  pricingType?: PricingType,
): PricingType | undefined {
  if (pricingType) return pricingType;
  if (!category) return undefined;
  const defaultType = CATEGORY_PRICING_DEFAULTS[category]?.defaultPricingType;
  if (!defaultType || defaultType === "custom") return undefined;
  return defaultType as PricingType;
}

/**
 * Resolve a catalog vendor's effective total cost for an event, multiplying
 * by guest count when the vendor is per-person (or in a per-person-default
 * category like Venue/Caterer with no explicit pricingType).
 *
 * Use this anywhere code sums catalog vendors (`startingPrice`) — recommendation
 * tiles, swipe summaries, onboarding totals, Evie budget analysis. EventVendor
 * sums (which carry quotedPrice) use `getVendorEffectivePrice` from eventService.
 *
 * Hourly is intentionally NOT multiplied here — `event.duration` is currently a
 * free-text string ("8 hours", "3 sets"), not a numeric hour count, so the
 * multiplier is unsafe. Tracked as follow-up.
 */
export function getCatalogVendorEffectivePrice(
  vendor: { startingPrice: number; pricingType?: PricingType; category?: string },
  guestCount?: number | null,
): number {
  const effectiveType = getEffectivePricingType(vendor.category, vendor.pricingType);
  if (effectiveType === "per_person" && guestCount && guestCount > 0) {
    return vendor.startingPrice * guestCount;
  }
  return vendor.startingPrice;
}

/**
 * Format a vendor's starting price with a pricing-type suffix.
 *
 *   per_person → "₪450/person"
 *   hourly     → "₪500/hr"
 *   flat       → "₪5,000"
 *
 * Pure function — pass i18n strings via `suffixes` if you need translations.
 * Mobile + web shims provide i18n-aware wrappers.
 */
export function formatStartingPrice(
  price: number,
  pricingType?: PricingType,
  suffixes?: { perPerson?: string; perHour?: string },
): string {
  if (pricingType === "per_person") {
    return `${formatPrice(price)}${suffixes?.perPerson ?? "/person"}`;
  }
  if (pricingType === "hourly") {
    return `${formatPrice(price)}${suffixes?.perHour ?? "/hr"}`;
  }
  return formatPrice(price);
}
